import { ValidationRule } from 'react-hook-form';

import { EMAIL_REGEX } from 'utils/validators';

import { RegExpPattern } from './typing';

/** Date formats that can be validated.
    @deprecated - import from components/molecules/form
*/
export enum DatePatterns {
  /*
   * Date formats must:
   * - use `dd` for the day position, `MM` for the month position, and `yyyy` for the year position
   * - use delimiters (ddMMyyyy is not allowed)
   * - use identical delimiters between positions (dd.MM-yyyy is not allowed)
   *
   * If any of these restrictions are to be lifted, dateValidatorFactory must be updated.
   */
  default = 'MM/dd/yyyy',
  dots = 'MM.dd.yyyy',
  reverse = 'dd/MM/yyyy',
}

// region validator plumbing

/** A function that returns true if the input value is valid, or a string describing the problem with the value otherwise. */
type Validator<T> = (value: T) => true | string;

/**
 * Default message shown to user when input does not match regex pattern.
 * @param regExp The regular expression object
 */
const DEFAULT_REGEX_ERROR_MESSAGE = (regExp: RegExp): string =>
  `This field must satisfy the pattern ${regExp.source}`;

/**
 * Translating from our regex object to the regex object react-hook-form understands, i.e., ValidationRule
 * @param regExpPattern our regex object, with the regex itself and an optional error message
 * @deprecated - import from components/molecules/form
 */
export const translatePatternToReactHookPattern = (
  regExpPattern: RegExpPattern | undefined
): ValidationRule<RegExp> | undefined =>
  regExpPattern?.regExp && {
    value: regExpPattern.regExp,
    message:
      regExpPattern.errorMessage ??
      DEFAULT_REGEX_ERROR_MESSAGE(regExpPattern.regExp),
  };
/** Validators must return true if the string is empty or valid, or a string otherwise.
 *  @deprecated - import from components/molecules/form
 */
type StringValidator = Validator<string>;

/** A wrapper function for Validators that treats empty strings and nullish values as valid.
 *  @deprecated - import from components/molecules/form
 */
const handleEmptyValue =
  <T>(validator: Validator<T>): Validator<T> =>
  (value: T | undefined | null) =>
    value === '' || value === undefined || value === null || validator(value);

/** A helper that makes it easy to apply validators conditionally
 *  @deprecated - import from components/molecules/form
 */
export const checkIf =
  (validatorApplies: boolean) =>
  <T>(validator: Validator<T>): Validator<T> =>
    validatorApplies ? validator : () => true;

/** Creates a Validator for a given date format. (Empty values are valid.)
 *  @deprecated - import from components/molecules/form
 */
export const dateValidatorFactory = (
  datePattern: DatePatterns
): StringValidator => {
  const [, fv1, fd1, fv2, , fv3] =
    datePattern.match(/(\w+)([./])(\w+)([./])(\w+)/) ?? [];

  const formatValues = [fv1, fv2, fv3];

  const dayPosition = formatValues.indexOf('dd');
  const monthPosition = formatValues.indexOf('MM');
  const yearPosition = formatValues.indexOf('yyyy');

  return handleEmptyValue((value) => {
    const [match, pv1, pd1, pv2, pd2, pv3] =
      value.match(/^(\d+)([./])(\d+)([./])(\d+)$/) ?? [];

    if (match === undefined) return `Unexpected format. Use ${datePattern}.`;

    if (fd1 !== pd1 || fd1 !== pd2) return `Use ${fd1} to delimit the date.`;

    const rawValues = [pv1, pv2, pv3];

    if (rawValues[dayPosition].length !== 2) return 'Day must be 2 digits';
    if (rawValues[monthPosition].length !== 2) return 'Month must be 2 digits';
    if (rawValues[yearPosition].length !== 4)
      return `Years must be four digits.`;

    const parsedValues = rawValues.map((val) => parseInt(val, 10));

    const dayValue = parsedValues[dayPosition];
    const monthValue = parsedValues[monthPosition];
    const yearValue = parsedValues[yearPosition];

    if (dayValue <= 0) return 'Day must be a positive number';
    if (monthValue <= 0) return 'Month must be a positive number';

    const parsedDate = new Date(yearValue, monthValue - 1, dayValue);
    parsedDate.setFullYear(yearValue);

    // checking that the parsed date is the same as the entered date, because Date will let us do stupid things
    if (
      Number.isNaN(parsedDate.valueOf()) ||
      parsedDate.getDate() !== dayValue ||
      parsedDate.getMonth() + 1 !== monthValue ||
      parsedDate.getFullYear() !== yearValue
    )
      return `Invalid date.`;

    return true;
  });
};

/** @deprecated - import from components/molecules/form */
const regexpValidatorFactory = (
  expression: RegExp,
  errMessage: string
): StringValidator =>
  handleEmptyValue((value: string) => expression.test(value) || errMessage);

/** @deprecated - import from components/molecules/form */
export const optionValidatorFactory = (options: string[]): StringValidator =>
  handleEmptyValue(
    (value: string) =>
      options.indexOf(value) !== -1 ||
      `Input must be one of: ${options.join(', ')}`
  );

/** @deprecated */
export const maxFileSizeValidatorFactory = (
  sizeMegabytes: number
): Validator<Blob> =>
  handleEmptyValue(
    (value) =>
      value.size < sizeMegabytes * (10 ^ 7) ||
      `Value is too large. Must be smaller than ${sizeMegabytes} MB.`
  );

// region validators
/*
Notes for organization:
  - All validator names should fit the pattern {type}Validator
  - Validators should be alphabetized; validators with similar purposes should be named such that they alphabetize together
*/

/** @deprecated - import from components/molecules/form */
export const emailValidator = regexpValidatorFactory(
  EMAIL_REGEX,
  'This field must contain a valid email address.'
);

/** @deprecated - import from components/molecules/form */
export const telValidator = regexpValidatorFactory(
  /^\d{10}$/,
  'This field must contain a 10-digit phone number (digits only).'
);

/** @deprecated - import from components/molecules/form */
export const trimmedStringValidator: StringValidator = handleEmptyValue(
  (value) => {
    if (value.length === value.trim().length) return true;

    const hasLeadingWhitespace = value.length !== value.trimStart().length;
    const hasTrailingWhitespace = value.length !== value.trimEnd().length;

    if (hasLeadingWhitespace && hasTrailingWhitespace)
      return 'Trim leading and trailing whitespace.';

    if (hasLeadingWhitespace) return 'Trim leading whitespace.';
    if (hasTrailingWhitespace) return 'Trim trailing whitespace.';

    return true;
  }
);

/** @deprecated - import from components/molecules/form */
export const urlValidator: StringValidator = handleEmptyValue((value) => {
  try {
    new URL(value);
    return true;
  } catch {
    return 'This field must contain a valid URL.';
  }
});
